import { ErrorHandler, inject, Injectable, Injector, NgZone } from '@angular/core';
import { HttpErrorResponse }                                  from '@angular/common/http';
import { Router }                                             from '@angular/router';

import { ErrorService }              from './error.service';
import { DcpError, DcpErrorHandler } from './dcp-error-model';
import { HttpStatusCode }            from './http-status-codes';
import { RestService }               from '../rest.service';
import { environment }               from '../../../environments/environment';
import { CacheUtils }                from '../../shared/utils/cache.utils';
import { InternalRoutes }            from '../../shared/data/internal-routes';

const DCP_ERROR_HANDLERS: DcpErrorHandler[] = [
  {dcpError: DcpError.ACCOUNT_NOT_FOUND, redirectTo: [InternalRoutes.ERROR]},
  {dcpError: DcpError.FORBIDDEN, redirectTo: [InternalRoutes.ERROR]},
  {dcpError: DcpError.MAINTENANCE, redirectTo: ['/maintenance']},
  {dcpError: DcpError.NOT_FOUND, redirectTo: [InternalRoutes.ERROR + HttpStatusCode.NOT_FOUND]},
  {dcpError: DcpError.UNAUTHORIZED, redirectTo: ['/logout']},
  {dcpError: DcpError.UNKNOWN, redirectTo: [InternalRoutes.ERROR]},
  {dcpError: DcpError.ERR_PING_MIS_CONFIG_EXCEPTION, logError: true},
];

export const errorMessageList = [];

@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private injector = inject(Injector);
  private zone = inject(NgZone);

  private errorService = this.injector.get(ErrorService);
  private restService = this.injector.get(RestService);

  handleError(error: Error) {
    errorMessageList.push(error.message);
    if (error instanceof HttpErrorResponse) {
      const dcpError = this.errorService.getDcpError(error);
      const errorHandler = dcpError !== undefined
        ? DCP_ERROR_HANDLERS.find(value => value.dcpError === dcpError)
        : undefined;

      const serviceUnavailable = error && (error.status === HttpStatusCode.BAD_GATEWAY || error.status === HttpStatusCode.SERVICE_UNAVAILABLE)
        && (error.url.endsWith(environment.api.endpoints.failoverStatusCheck)
          || error.url.endsWith(environment.api.endpoints.sessionMetadata)
          || error.url.endsWith(environment.api.endpoints.csrf));

      if (errorHandler !== undefined) {
        if (errorHandler.redirectTo) {
          CacheUtils.evict();
          this.navigateTo(errorHandler.redirectTo);
        }

        if (errorHandler.logError) {
          console.error(error);
        }
      } else if (serviceUnavailable) {
        console.error(error);
      } else {
        this.handleUnknownError(error);
      }
    } else {
      this.handleUnknownError(error);
    }
  }

  private navigateTo(routerCommands: unknown[]): void {
    const router = this.injector.get(Router);
    this.zone.run(() => router.navigate(routerCommands).then()).then();
  }

  private handleUnknownError(error: Error): void {
    this.restService.post(environment.api.endpoints.clientLogs.error, {error: JSON.stringify(error, Object.getOwnPropertyNames(error))})
      .subscribe({
        next: _ => {
          console.error(error);
          CacheUtils.evict();
          this.navigateTo([InternalRoutes.ERROR]);
        },
        error: error => {
          if (!(error instanceof HttpErrorResponse) || error.status !== HttpStatusCode.UNAUTHORIZED) {
            console.error(error);
          }
        }
      });
  }
}